"use client";
import { getImageUrl } from "@/lib/getImageUrl";
import { deleteTrialReminder, getTrialReminder } from "@/lib/trialReminder";
import { Box, Button, Typography, useTheme } from "@mui/material";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export default function TrialWelcomeModal() {
  const theme = useTheme();
  const t = useTranslations("TrialWelcome");
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [docHeight, setDocHeight] = useState<number>(0);
  const cardRef = useRef<HTMLDivElement | null>(null);

  // 标记已挂载（避免 SSR hydration 问题）
  useEffect(() => {
    setMounted(true);
    // 计算文档总高度用于遮罩覆盖
    const updateHeight = () => setDocHeight(document.documentElement.scrollHeight);
    updateHeight();
    window.addEventListener("resize", updateHeight);
    // 适配内容动态变化（简单监听 scroll 再次计算）
    const onScroll = () => updateHeight();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("resize", updateHeight);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  useEffect(() => {
    (async () => {
      const res = await getTrialReminder();
      if (res.show) {
        setOpen(true);
        await deleteTrialReminder();
      }
    })();
  }, []);

  // 点击外部关闭（不阻止滚动，因为不依赖 pointerEvents 捕获）
  useEffect(() => {
    if (!open) return;
    const handleOutside = (e: MouseEvent | TouchEvent) => {
      if (!cardRef.current) return;
      if (!cardRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("touchstart", handleOutside, { passive: true });
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("touchstart", handleOutside);
    };
  }, [open]);

  if (!mounted || !open) return null;

  const node = (
    <Box
      sx={{
        position: "absolute", // 随页面滚动
        top: 0,
        left: 0,
        width: "100%",
        zIndex: 1300,
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        pointerEvents: "none",
      }}
      aria-hidden={!open}>
      {/* 背景遮罩（不阻止滚动） */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: docHeight ? `${docHeight}px` : "100vh",
          background: "rgba(0,0,0,0.32)",
          backdropFilter: "blur(6px)",
          pointerEvents: "none",
        }}
      />
      <Box
        id="trial-welcome-card"
        sx={{
          position: "relative",
          maxWidth: "37.125rem",
          width: "100%",
          mt: { xs: 6, md: 10 },
          mx: { xs: "0.5rem", md: "auto" },
          px: "1.5rem",
          pt: "1.75rem",
          pb: "3rem",
          borderRadius: "0.75rem",
          border: theme.palette.mode === "dark" ? "1px solid #2D3135" : "1px solid #E1E4E5",
          boxShadow: "0 4px 20px 8px rgba(0, 0, 0, 0.15)",
          bgcolor: theme.palette.mode === "dark" ? "rgba(35, 38, 41, 0.80)" : "rgba(255, 255, 255, 0.80)",
          pointerEvents: "auto",
        }}
        role="dialog"
        aria-modal={false}
        aria-label="Trial welcome"
        ref={cardRef}>
        <Box sx={{ width: "17.5rem", height: "17.5rem", margin: "0 auto" }}>
          <img src={getImageUrl("trial-gift.png", theme.palette.mode)} alt="Trial Welcome" />
        </Box>

        {/* Heading */}
        <Typography
          sx={{ fontSize: "var(--font-size-xl)", fontWeight: 600, lineHeight: 1.5, mt: 1, color: "primary.main" }}>
          {t.rich("heading", {
            highlight: (children) => (
              <Box component="span" sx={{ color: "text.primary" }}>
                {children}
              </Box>
            ),
            br: () => <br />,
          })}
        </Typography>

        {/* Bullet access intro */}
        <Typography sx={{ fontSize: "var(--font-size-xs)", fontWeight: 600, lineHeight: 1.6, mt: "1.5rem" }}>
          {t("intro")}
        </Typography>
        <Box component="ul" sx={{ mt: "0.75rem", pl: "0.5rem" }}>
          {(["bullets.signals", "bullets.data", "bullets.research", "bullets.tools", "bullets.lessons"] as const).map(
            (key) => (
              <Box component="li" key={key} sx={{ fontSize: "var(--font-size-xs)", fontWeight: 600, lineHeight: 2 }}>
                <span style={{ marginRight: "0.5rem" }}>·</span>
                {t(key)}
              </Box>
            )
          )}
        </Box>

        {/* Paragraphs */}
        <Typography sx={{ fontSize: "var(--font-size-xs)", lineHeight: 1.6, color: "text.secondary", mt: "1.5rem" }}>
          {t("body.p1")}
        </Typography>
        <Typography sx={{ fontSize: "var(--font-size-xs)", lineHeight: 1.6, color: "text.secondary", mt: "0.75rem" }}>
          {t("body.p2")}
        </Typography>

        <Box textAlign="center" sx={{ mt: 3 }}>
          <Button
            onClick={() => setOpen(false)}
            sx={(theme) => ({
              maxWidth: "23rem",
              width: "100%",
              height: "2.375rem",
              fontWeight: 600,
              fontSize: "var(--font-size-xs)",
              backgroundColor: theme.palette.mode === "dark" ? "#F2F2F2" : "#2D3135",
              color: theme.palette.mode === "dark" ? "#161A1E" : "#F2F2F2",
              borderRadius: "3rem",
            })}>
            {t("button.primary")}
          </Button>
        </Box>
      </Box>
    </Box>
  );

  return createPortal(node, document.body);
}
